import React, { useEffect, useState } from "react";
import {
  Users as UsersIcon, UserPlus, Trash2, Building2, Plus, ShieldCheck, GraduationCap,
  CheckCircle2, XCircle, Loader2,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import { Card, Section, Btn, Pill } from "./ui";

export default function Users() {
  const { session } = useAuth();
  const tid = session.tenant.id;
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDept, setNewDept] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "demo123", role: "employee", department_id: "" });
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.users(tid), api.departments(tid)]).then(([u, d]) => {
      setUsers(u); setDepts(d); setLoading(false);
    });
  };
  useEffect(load, [tid]);

  const addDept = async () => {
    if (!newDept.trim()) return;
    await api.addDepartment(tid, { name: newDept.trim() });
    setNewDept(""); load();
  };
  const addUser = async () => {
    setErr("");
    if (!form.name || !form.email) { setErr("Name and email required"); return; }
    try {
      await api.addUser(tid, { ...form, department_id: form.department_id || null });
      setForm({ name: "", email: "", password: "demo123", role: "employee", department_id: "" });
      load();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not add user"); }
  };

  return (
    <Section title="Users &amp; Departments" subtitle="Role-based access — Admins configure, Employees use Gateway Chat. Includes AI policy sign-off evidence.">
      {loading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Departments */}
          <Card className="p-5">
            <div className="mb-4 flex items-center gap-2"><Building2 className="h-4 w-4 text-brand" /><h3 className="font-display text-sm font-bold">Departments</h3></div>
            <div className="mb-4 flex gap-2">
              <input data-testid="new-dept-input" value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="e.g. Corporate Law" className="flex-1 rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm outline-none focus:border-brand" />
              <Btn data-testid="add-dept-btn" onClick={addDept}><Plus className="h-4 w-4" /></Btn>
            </div>
            <div className="space-y-2">
              {depts.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-lg border border-subtle bg-elevated/50 px-3 py-2">
                  <span className="text-sm text-slate-200">{d.name}</span>
                  <button data-testid={`del-dept-${d.id}`} onClick={async () => { await api.delDepartment(d.id); load(); }} className="text-slate-500 hover:text-block"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {depts.length === 0 && <p className="text-xs text-slate-600">No departments yet.</p>}
            </div>
          </Card>

          {/* Add user */}
          <Card className="p-5">
            <div className="mb-4 flex items-center gap-2"><UserPlus className="h-4 w-4 text-brand" /><h3 className="font-display text-sm font-bold">Add user</h3></div>
            <div className="space-y-3">
              <input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm outline-none focus:border-brand" />
              <input data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@firm.example" className="w-full rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm outline-none focus:border-brand" />
              <input data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="password" className="w-full rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm outline-none focus:border-brand" />
              <div className="grid grid-cols-2 gap-2">
                <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm outline-none focus:border-brand">
                  <option value="employee">Employee</option>
                  <option value="admin">Admin</option>
                </select>
                <select data-testid="user-dept" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} className="rounded-lg border border-subtle bg-elevated px-3 py-2 text-sm outline-none focus:border-brand">
                  <option value="">No department</option>
                  {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              {err && <p className="text-xs text-block">{err}</p>}
              <Btn data-testid="add-user-btn" onClick={addUser} className="w-full"><UserPlus className="h-4 w-4" /> Add user</Btn>
            </div>
          </Card>

          {/* Sign-off summary */}
          <Card className="p-5">
            <div className="mb-4 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand" /><h3 className="font-display text-sm font-bold">Evidence dossier</h3></div>
            <p className="mb-3 text-[11px] text-slate-500">Per-user AI policy sign-off — the record an admin could show an auditor.</p>
            <div className="space-y-2">
              <Row label="Total users" value={users.length} />
              <Row label="Signed AI policy" value={users.filter((u) => u.signed_at).length} tone="allow" />
              <Row label="Pending sign-off" value={users.filter((u) => !u.signed_at).length} tone="warn" />
            </div>
          </Card>

          {/* User table */}
          <Card className="overflow-x-auto p-0 lg:col-span-3 custom-scrollbar">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b border-subtle bg-elevated/50 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Department</th><th className="px-4 py-3">AI policy sign-off</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid="user-row" className="border-b border-subtle/50 hover:bg-white/5">
                    <td className="px-4 py-3 text-white">{u.name}</td>
                    <td className="px-4 py-3 font-mono text-[12px] text-slate-400">{u.email}</td>
                    <td className="px-4 py-3">{u.role === "admin" ? <Pill tone="gold"><ShieldCheck className="h-3 w-3" />Admin</Pill> : <Pill tone="muted">Employee</Pill>}</td>
                    <td className="px-4 py-3 text-slate-400">{u.department_name || "—"}</td>
                    <td className="px-4 py-3">
                      {u.signed_at ? (
                        <span className="flex items-center gap-1.5 text-xs text-allow"><CheckCircle2 className="h-3.5 w-3.5" /> Signed {new Date(u.signed_at).toLocaleDateString("en-GB")} · quiz {u.training_score ?? "—"}/3</span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-xs text-warn"><XCircle className="h-3.5 w-3.5" /> Not signed</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {u.id !== session.user.id && (
                        <button data-testid={`del-user-${u.id}`} onClick={async () => { await api.delUser(u.id); load(); }} className="text-slate-500 hover:text-block"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </Section>
  );
}

const Row = ({ label, value, tone = "muted" }) => {
  const c = { muted: "#94A3B8", allow: "#10B981", warn: "#F59E0B" }[tone];
  return (
    <div className="flex items-center justify-between rounded-lg border border-subtle bg-elevated/50 px-3 py-2">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="font-display text-lg font-bold" style={{ color: c }}>{value}</span>
    </div>
  );
};
